import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  HStack,
  VStack,
  Spinner,
  Center,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  IconButton,
  Tooltip,
  Textarea,
  Select,
  SimpleGrid,
  Card,
  CardBody,
  Checkbox,
  Radio,
  RadioGroup,
  Stack,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiSend, FiTrash2, FiClock, FiUsers, FiSave, FiFilter, FiBookmark, FiX } from 'react-icons/fi';
import { collection, getDocs, addDoc, deleteDoc, doc, query, orderBy, Timestamp, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { sendNotificationToUsers, getNotificationType } from '../../services/notifications';
import { getAllUsers, User } from '../../services/firestore';

// Cast icons as React.ElementType to fix TypeScript issues
const FiSendIcon = FiSend as React.ElementType;
const FiTrash2Icon = FiTrash2 as React.ElementType;
const FiClockIcon = FiClock as React.ElementType;
const FiUsersIcon = FiUsers as React.ElementType;
const FiSaveIcon = FiSave as React.ElementType;
const FiFilterIcon = FiFilter as React.ElementType;
const FiBookmarkIcon = FiBookmark as React.ElementType;
const FiXIcon = FiX as React.ElementType;

interface Notification {
  id: string;
  title: string;
  message: string;
  targetAudience: 'all' | 'customers' | 'technicians' | 'specific';
  sentAt: Timestamp;
  status: 'sent' | 'scheduled' | 'failed';
  sentBy: string;
}

interface NotificationTemplate {
  id: string;
  name: string;
  title: string;
  message: string;
  targetAudience: 'all' | 'customers' | 'technicians';
}

const NotificationsManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [scheduled, setScheduled] = useState<any[]>([]);
  
  const [showSendModal, setShowSendModal] = useState(false);
  const [notifForm, setNotifForm] = useState({
    title: '',
    message: '',
    targetAudience: 'all' as 'all' | 'customers' | 'technicians' | 'specific' | 'single',
    segments: {
      inactiveDays: 0,
      roles: [] as Array<'admin' | 'technician' | 'customer'>,
    },
    templateId: '',
    scheduleType: 'now' as 'now' | 'later',
    scheduledAt: '', // ISO local datetime string
    notificationType: 'system' as 'system' | 'booking_update' | 'pool_invite' | 'technician_update' | 'payment_required',
    context: '' as '' | 'booking' | 'pool' | 'technician' | 'payment' | 'marketing',
    singleUserId: '', // For single user selection
    singleUserSearch: '', // For searching users
  });
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({ firstName: 'John', lastName: 'Doe' });
  const [testRecipient, setTestRecipient] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [deliveryLogs, setDeliveryLogs] = useState<any[]>([]);
  const [segmentsSaved, setSegmentsSaved] = useState<any[]>([]);
  const [savingSegment, setSavingSegment] = useState(false);
  const { isOpen: isSchedOpen, onOpen: onSchedOpen, onClose: onSchedClose } = useDisclosure();
  const [schedForm, setSchedForm] = useState<any>({ 
    id: '', 
    title: '', 
    message: '', 
    targetAudience: 'all', 
    scheduledAt: '', 
    status: 'scheduled',
    notificationType: 'system',
    context: '',
  });

  useEffect(() => {
    fetchNotifications();
    fetchTemplates();
    fetchDeliveryLogs();
    fetchSegments();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (notifForm.singleUserSearch) {
      const search = notifForm.singleUserSearch.toLowerCase();
      const filtered = allUsers.filter(user => 
        user.email?.toLowerCase().includes(search) ||
        user.firstName?.toLowerCase().includes(search) ||
        user.lastName?.toLowerCase().includes(search) ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(search) ||
        user.id.toLowerCase().includes(search)
      );
      setFilteredUsers(filtered.slice(0, 10)); // Limit to 10 results
    } else {
      setFilteredUsers([]);
    }
  }, [notifForm.singleUserSearch, allUsers]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const notificationsQuery = query(collection(db, 'systemNotifications'), orderBy('sentAt', 'desc'));
      const querySnapshot = await getDocs(notificationsQuery);
      
      const notifs: Notification[] = [];
      querySnapshot.forEach((doc) => {
        notifs.push({ id: doc.id, ...doc.data() } as Notification);
      });
      
      setNotifications(notifs);
      // Load scheduled
      const scheduledSnap = await getDocs(query(collection(db, 'scheduledNotifications'), orderBy('scheduledAt', 'asc')));
      const pend: any[] = [];
      scheduledSnap.forEach(d => pend.push({ id: d.id, ...d.data() }));
      setScheduled(pend);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast({
        title: 'Error loading notifications',
        description: 'Failed to fetch notification history',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryLogs = async () => {
    try {
      const logsSnap = await getDocs(query(collection(db, 'notificationDeliveries'), orderBy('timestamp', 'desc')));
      const logs: any[] = [];
      logsSnap.forEach(d => logs.push({ id: d.id, ...d.data() }));
      setDeliveryLogs(logs);
    } catch (e) {
      // ignore
    }
  };

  const fetchTemplates = async () => {
    try {
      const snap = await getDocs(collection(db, 'notificationTemplates'));
      const list: NotificationTemplate[] = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as NotificationTemplate));
      setTemplates(list);
    } catch (e) {
      // no-op
    }
  };

  const fetchSegments = async () => {
    try {
      const snap = await getDocs(collection(db, 'notificationSegments'));
      const list: any[] = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setSegmentsSaved(list);
    } catch (e) {
      // ignore
    }
  };

  const handleSendNotification = async () => {
    if (!notifForm.title || !notifForm.message) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in title and message',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate single user selection if targetAudience is 'single'
    if (notifForm.targetAudience === 'single' && !notifForm.singleUserId) {
      toast({
        title: 'Validation Error',
        description: 'Please select a user to send the notification to',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSending(true);
    try {
      if (notifForm.scheduleType === 'later' && notifForm.scheduledAt) {
        // Schedule for later - store in scheduledNotifications
        const when = new Date(notifForm.scheduledAt);
        const data = {
          title: notifForm.title,
          message: notifForm.message,
          targetAudience: notifForm.targetAudience,
          segments: notifForm.segments,
          notificationType: notifForm.notificationType,
          context: notifForm.context,
          singleUserId: notifForm.singleUserId || null,
          scheduledAt: Timestamp.fromDate(when),
          status: 'scheduled' as const,
          createdAt: Timestamp.now(),
          createdBy: currentUser?.uid || 'admin',
        };
        await addDoc(collection(db, 'scheduledNotifications'), data);
        
        toast({
          title: 'Success',
          description: 'Notification scheduled successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Send immediately - create user notifications
        let result;
        
        if (notifForm.targetAudience === 'single' && notifForm.singleUserId) {
          // Send to single user
          result = await sendNotificationToUsers(
            'specific',
            {
              type: notifForm.notificationType,
              title: notifForm.title,
              body: notifForm.message,
              context: notifForm.context,
            },
            {
              roles: [],
              userIds: [notifForm.singleUserId], // Pass specific user ID
            },
            currentUser?.uid || 'admin'
          );
        } else {
          // Send to audience/segments
          result = await sendNotificationToUsers(
            notifForm.targetAudience,
            {
              type: notifForm.notificationType,
              title: notifForm.title,
              body: notifForm.message, // Map "message" to "body" for web-app
              context: notifForm.context,
            },
            notifForm.segments,
            currentUser?.uid || 'admin'
          );
        }

        toast({
          title: 'Success',
          description: `Notification sent to ${result.userCount} user${result.userCount !== 1 ? 's' : ''}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      setNotifForm({ 
        title: '', 
        message: '', 
        targetAudience: 'all', 
        segments: { inactiveDays: 0, roles: [] }, 
        templateId: '', 
        scheduleType: 'now', 
        scheduledAt: '',
        notificationType: 'system',
        context: '',
        singleUserId: '',
        singleUserSearch: '',
      });
      setShowSendModal(false);
      fetchNotifications();
    } catch (error: any) {
      console.error('Error sending notification:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send notification',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  const handleApplyTemplate = (templateId: string) => {
    setNotifForm(prev => ({ ...prev, templateId }));
    const t = templates.find(t => t.id === templateId);
    if (t) {
      setNotifForm(prev => ({ ...prev, title: t.title, message: t.message, targetAudience: t.targetAudience }));
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!notifForm.title || !notifForm.message) {
      toast({ title: 'Validation', description: 'Title and message required to save template', status: 'error', duration: 2000, isClosable: true });
      return;
    }
    setSavingTemplate(true);
    try {
      const name = notifForm.title.slice(0, 40);
      const payload = {
        name,
        title: notifForm.title,
        message: notifForm.message,
        targetAudience: notifForm.targetAudience as 'all' | 'customers' | 'technicians',
        createdAt: Timestamp.now(),
        createdBy: currentUser?.uid || 'admin',
      };
      const ref = await addDoc(collection(db, 'notificationTemplates'), payload);
      setTemplates([{ id: ref.id, ...(payload as any) }, ...templates]);
      toast({ title: 'Template saved', status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: 'Save failed', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleApplySegment = (segmentId: string) => {
    const seg = segmentsSaved.find(s => s.id === segmentId);
    if (seg) {
      setNotifForm(prev => ({ ...prev, segments: { ...prev.segments, ...(seg.criteria || {}) } }));
    }
  };

  const handleSaveSegment = async () => {
    setSavingSegment(true);
    try {
      const payload = {
        name: notifForm.title ? notifForm.title.slice(0, 32) : 'Segment',
        criteria: notifForm.segments,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.uid || 'admin',
      };
      const ref = await addDoc(collection(db, 'notificationSegments'), payload);
      setSegmentsSaved([{ id: ref.id, ...payload }, ...segmentsSaved]);
      toast({ title: 'Segment saved', status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: 'Save failed', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSavingSegment(false);
    }
  };

  const renderWithVariables = (text: string) => {
    return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (previewVariables[key] ?? ''));
  };

  const handleSendTest = async () => {
    if (!testRecipient.trim()) {
      toast({ title: 'Recipient required', status: 'error', duration: 2000, isClosable: true });
      return;
    }
    setSendingTest(true);
    try {
      const payload = {
        title: renderWithVariables(notifForm.title),
        message: renderWithVariables(notifForm.message),
        to: testRecipient.trim(),
        createdAt: Timestamp.now(),
        createdBy: currentUser?.uid || 'admin',
      };
      await addDoc(collection(db, 'testNotifications'), payload);
      toast({ title: 'Test queued', status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: 'Test failed', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSendingTest(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'systemNotifications', id));
      toast({
        title: 'Success',
        description: 'Notification deleted',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete notification',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteScheduled = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'scheduledNotifications', id));
      setScheduled((prev) => prev.filter((s) => s.id !== id));
      toast({ title: 'Scheduled notification deleted', status: 'success', duration: 2000, isClosable: true });
    } catch (error) {
      console.error('Error deleting scheduled notification:', error);
      toast({ title: 'Error', description: 'Failed to delete scheduled notification', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleOpenScheduled = (s: any) => {
    setSchedForm({
      id: s.id,
      title: s.title || '',
      message: s.message || '',
      targetAudience: s.targetAudience || 'all',
      notificationType: s.notificationType || 'system',
      context: s.context || '',
      scheduledAt: (s.scheduledAt?.toDate ? s.scheduledAt.toDate() : new Date(s.scheduledAt)).toISOString().slice(0,16),
      status: s.status || 'scheduled'
    });
    onSchedOpen();
  };

  const handleSaveScheduled = async () => {
    if (!schedForm.id) return;
    try {
      await setDoc(doc(db, 'scheduledNotifications', schedForm.id), {
        title: schedForm.title,
        message: schedForm.message,
        targetAudience: schedForm.targetAudience,
        notificationType: schedForm.notificationType || 'system',
        context: schedForm.context || '',
        scheduledAt: Timestamp.fromDate(new Date(schedForm.scheduledAt)),
        status: 'scheduled'
      }, { merge: true });
      setScheduled(prev => prev.map(p => p.id === schedForm.id ? { ...p, ...schedForm, scheduledAt: new Date(schedForm.scheduledAt) } : p));
      toast({ title: 'Scheduled notification updated', status: 'success', duration: 2000, isClosable: true });
      onSchedClose();
    } catch (e) {
      toast({ title: 'Update failed', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const getAudienceBadgeColor = (audience: string) => {
    switch (audience) {
      case 'all':
        return 'blue';
      case 'customers':
        return 'green';
      case 'technicians':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
          flex="1"
          minW="200px"
        >
          System Notifications
        </Heading>
        <Button
          leftIcon={<FiSendIcon />}
          colorScheme="blue"
          onClick={() => setShowSendModal(true)}
        >
          Send Notification
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Card>
          <CardBody>
            <HStack>
              <Box p={3} borderRadius="lg" bg="blue.50" color="blue.600">
                <FiUsersIcon size={24} />
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">Total Sent</Text>
                <Text fontSize="2xl" fontWeight="bold">{notifications.length}</Text>
              </Box>
            </HStack>
          </CardBody>
        </Card>

      {/* (moved) Scheduled/Pending section appears below history */}
        <Card>
          <CardBody>
            <HStack>
              <Box p={3} borderRadius="lg" bg="green.50" color="green.600">
                <FiClockIcon size={24} />
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">This Week</Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {notifications.filter(n => {
                    let sentDate: Date;
                    if (n.sentAt && typeof n.sentAt === 'object' && 'toDate' in n.sentAt) {
                      sentDate = (n.sentAt as any).toDate();
                    } else if (n.sentAt) {
                      sentDate = new Date(n.sentAt as any);
                    } else {
                      return false;
                    }
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return sentDate >= weekAgo;
                  }).length}
                </Text>
              </Box>
            </HStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <HStack>
              <Box p={3} borderRadius="lg" bg="purple.50" color="purple.600">
                <FiSendIcon size={24} />
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">Successful</Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {notifications.filter(n => n.status === 'sent').length}
                </Text>
              </Box>
            </HStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card>
        <CardBody p={0}>
          {notifications.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text color="gray.500">No notifications sent yet</Text>
            </Box>
          ) : (
            <Box 
              overflowX="auto" 
              overflowY="auto"
              maxH="calc(100vh - 400px)"
            >
              <Table variant="simple" minW="100%">
              <Thead>
                <Tr>
                  <Th>Title</Th>
                  <Th>Message</Th>
                  <Th>Audience</Th>
                  <Th>Status</Th>
                  <Th>Sent At</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {notifications.map((notif) => (
                  <Tr key={notif.id}>
                    <Td fontWeight="medium">{notif.title}</Td>
                    <Td>
                      <Text noOfLines={2} maxW="300px">
                        {notif.message}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={getAudienceBadgeColor(notif.targetAudience)}>
                        {notif.targetAudience}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={notif.status === 'sent' ? 'green' : 'gray'}>
                        {notif.status}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{formatDate(notif.sentAt)}</Text>
                    </Td>
                    <Td>
                      <Tooltip label="Delete">
                        <IconButton
                          aria-label="Delete"
                          icon={<FiTrash2Icon />}
                          size="sm"
                          colorScheme="red"
                          minH="44px"
                          minW="44px"
                          onClick={() => handleDelete(notif.id)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Scheduled/Pending (history-adjacent) */}
      <Card mt={6}>
        <CardBody>
          <Heading size="md" mb={4}>Scheduled Notifications</Heading>
          {scheduled.length === 0 ? (
            <Text color="gray.500">No scheduled notifications</Text>
          ) : (
            <Box 
              overflowX="auto" 
              overflowY="auto"
              maxH="calc(100vh - 400px)"
            >
              <Table variant="simple" minW="100%">
              <Thead>
                <Tr>
                  <Th>Title</Th>
                  <Th>Message</Th>
                  <Th>Audience</Th>
                  <Th>Status</Th>
                  <Th>Scheduled At</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {scheduled.map((s) => (
                  <Tr key={s.id}>
                    <Td>{s.title}</Td>
                    <Td>
                      <Button size="sm" variant="link" minH="44px" onClick={()=> handleOpenScheduled(s)}>
                        {s.status === 'scheduled' ? 'View / Edit' : 'View'}
                      </Button>
                    </Td>
                    <Td>
                      <Badge colorScheme={getAudienceBadgeColor(s.targetAudience)}>
                        {s.targetAudience}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={s.status === 'scheduled' ? 'yellow' : 'green'}>{s.status}</Badge>
                    </Td>
                    <Td>{formatDate(s.scheduledAt)}</Td>
                    <Td>
                      <Tooltip label="Delete">
                        <IconButton
                          aria-label="Delete scheduled"
                          icon={<FiTrash2Icon />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleDeleteScheduled(s.id)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Scheduled View/Edit Modal */}
      <Modal isOpen={isSchedOpen} onClose={onSchedClose} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{schedForm.status === 'scheduled' ? 'View / Edit Scheduled Notification' : 'View Scheduled Notification'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isDisabled={schedForm.status !== 'scheduled'}>
                <FormLabel>Title</FormLabel>
                <Input value={schedForm.title} onChange={(e)=> setSchedForm((p:any)=> ({ ...p, title: e.target.value }))} />
              </FormControl>
              <FormControl isDisabled={schedForm.status !== 'scheduled'}>
                <FormLabel>Message</FormLabel>
                <Textarea rows={5} value={schedForm.message} onChange={(e)=> setSchedForm((p:any)=> ({ ...p, message: e.target.value }))} />
              </FormControl>
              <HStack>
              <FormControl isDisabled={schedForm.status !== 'scheduled'}>
                <FormLabel>Audience</FormLabel>
                <Select value={schedForm.targetAudience} onChange={(e)=> setSchedForm((p:any)=> ({ ...p, targetAudience: e.target.value }))}>
                  <option value="all">All Users</option>
                  <option value="customers">Customers</option>
                  <option value="technicians">Technicians</option>
                </Select>
              </FormControl>
              <FormControl isDisabled={schedForm.status !== 'scheduled'}>
                <FormLabel>Notification Type</FormLabel>
                <Select 
                  value={schedForm.notificationType || 'system'} 
                  onChange={(e)=> {
                    const type = e.target.value;
                    setSchedForm((p:any)=> ({ 
                      ...p, 
                      notificationType: type,
                      context: type === 'booking_update' ? 'booking' :
                               type === 'pool_invite' ? 'pool' :
                               type === 'technician_update' ? 'technician' :
                               type === 'payment_required' ? 'payment' : 'marketing'
                    }));
                  }}
                >
                  <option value="system">System / General</option>
                  <option value="booking_update">Booking Update</option>
                  <option value="pool_invite">Pool Invite</option>
                  <option value="technician_update">Technician Update</option>
                  <option value="payment_required">Payment Required</option>
                </Select>
              </FormControl>
                <FormControl isDisabled={schedForm.status !== 'scheduled'}>
                  <FormLabel>Scheduled At</FormLabel>
                  <Input type="datetime-local" value={schedForm.scheduledAt} onChange={(e)=> setSchedForm((p:any)=> ({ ...p, scheduledAt: e.target.value }))} />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSchedClose}>Close</Button>
            {schedForm.status === 'scheduled' && (
              <Button colorScheme="blue" onClick={handleSaveScheduled}>Save</Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delivery Logs */}
      <Card mt={6}>
        <CardBody>
          <Heading size="md" mb={4}>Delivery Logs</Heading>
          {deliveryLogs.length === 0 ? (
            <Text color="gray.500">No delivery logs</Text>
          ) : (
            <Box 
              overflowX="auto" 
              overflowY="auto"
              maxH="calc(100vh - 400px)"
            >
              <Table variant="simple" minW="100%">
              <Thead>
                <Tr>
                  <Th>Recipient</Th>
                  <Th>Title</Th>
                  <Th>Status</Th>
                  <Th>Time</Th>
                </Tr>
              </Thead>
              <Tbody>
                {deliveryLogs.map((log) => (
                  <Tr key={log.id}>
                    <Td>{log.to || log.userId || '—'}</Td>
                    <Td>{log.title || '—'}</Td>
                    <Td>
                      <Badge colorScheme={log.status === 'delivered' ? 'green' : log.status === 'failed' ? 'red' : 'gray'}>
                        {log.status || 'queued'}
                      </Badge>
                    </Td>
                    <Td>{formatDate(log.timestamp)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Send Notification Modal */}
      <Modal isOpen={showSendModal} onClose={() => setShowSendModal(false)} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send Notification</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Template</FormLabel>
                <HStack>
                  <Select
                    placeholder="Select template"
                    value={notifForm.templateId}
                    onChange={(e)=> handleApplyTemplate(e.target.value)}
                  >
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Select>
                  <Tooltip label="Save as Template">
                    <IconButton
                      aria-label="Save as Template"
                      icon={<FiSaveIcon />}
                      onClick={handleSaveAsTemplate}
                      isLoading={savingTemplate}
                      colorScheme="blue"
                      variant="outline"
                    />
                  </Tooltip>
                </HStack>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  value={notifForm.title}
                  onChange={(e) => setNotifForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter notification title..."
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Message</FormLabel>
                <Textarea
                  value={notifForm.message}
                  onChange={(e) => setNotifForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter notification message..."
                  rows={5}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Notification Type</FormLabel>
                <Select
                  value={notifForm.notificationType}
                  onChange={(e) => {
                    const type = e.target.value as 'system' | 'booking_update' | 'pool_invite' | 'technician_update' | 'payment_required';
                    setNotifForm(prev => ({ 
                      ...prev, 
                      notificationType: type,
                      context: type === 'booking_update' ? 'booking' :
                               type === 'pool_invite' ? 'pool' :
                               type === 'technician_update' ? 'technician' :
                               type === 'payment_required' ? 'payment' : 'marketing'
                    }));
                  }}
                >
                  <option value="system">System / General</option>
                  <option value="booking_update">Booking Update</option>
                  <option value="pool_invite">Pool Invite</option>
                  <option value="technician_update">Technician Update</option>
                  <option value="payment_required">Payment Required</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  This determines how the notification appears and behaves in the web-app
                </Text>
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Heading size="sm" mb={2}>Template Variables</Heading>
                  <VStack spacing={2} align="stretch">
                    {['firstName','lastName'].map(key => (
                      <HStack key={key}>
                        <Box w="120px"><Text fontSize="sm">{key}</Text></Box>
                        <Input size="sm" value={previewVariables[key] || ''} minH="44px" onChange={(e)=> setPreviewVariables(prev => ({ ...prev, [key]: e.target.value }))} />
                      </HStack>
                    ))}
                    <Text fontSize="xs" color="gray.500">Use variables in title/message like {'{{firstName}}'}</Text>
                  </VStack>
                </Box>
                <Box>
                  <Heading size="sm" mb={2}>Preview</Heading>
                  <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Text fontWeight="bold" mb={2}>{renderWithVariables(notifForm.title) || '—'}</Text>
                    <Text whiteSpace="pre-wrap">{renderWithVariables(notifForm.message) || '—'}</Text>
                  </Box>
                </Box>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Target Audience</FormLabel>
                <RadioGroup
                  value={notifForm.targetAudience}
                  onChange={(value: any) => setNotifForm(prev => ({ 
                    ...prev, 
                    targetAudience: value,
                    singleUserId: value !== 'single' ? '' : prev.singleUserId, // Clear selection if switching away
                    singleUserSearch: value !== 'single' ? '' : prev.singleUserSearch,
                  }))}
                >
                  <Stack direction="row" spacing={4} flexWrap="wrap">
                    <Radio value="all">All Users</Radio>
                    <Radio value="customers">Customers Only</Radio>
                    <Radio value="technicians">Technicians Only</Radio>
                    <Radio value="single">Single User</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {notifForm.targetAudience === 'single' && (
                <FormControl isRequired={notifForm.targetAudience === 'single'}>
                  <FormLabel>Select User</FormLabel>
                  <Box position="relative">
                    <Input
                      placeholder="Search by name, email, or user ID..."
                      value={notifForm.singleUserSearch}
                      onChange={(e) => setNotifForm(prev => ({ 
                        ...prev, 
                        singleUserSearch: e.target.value,
                        singleUserId: '', // Clear selection when searching
                      }))}
                      onFocus={() => {
                        if (!notifForm.singleUserSearch || filteredUsers.length === 0) {
                          // Show first 10 users when focused (if no search or no results)
                          setFilteredUsers(allUsers.slice(0, 10));
                        }
                      }}
                    />
                    {(notifForm.singleUserSearch || filteredUsers.length > 0) && filteredUsers.length > 0 && (
                      <Box
                        position="absolute"
                        zIndex={1000}
                        w="100%"
                        mt={1}
                        bg="white"
                        borderWidth="1px"
                        borderRadius="md"
                        boxShadow="lg"
                        maxH="200px"
                        overflowY="auto"
                      >
                        {filteredUsers.map((user) => (
                          <Box
                            key={user.id}
                            p={3}
                            cursor="pointer"
                            _hover={{ bg: 'gray.100' }}
                            onClick={() => {
                              setNotifForm(prev => ({ 
                                ...prev, 
                                singleUserId: user.id,
                                singleUserSearch: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.id,
                              }));
                              setFilteredUsers([]);
                            }}
                            borderBottomWidth="1px"
                            borderColor="gray.200"
                          >
                            <Text fontWeight="medium">
                              {user.firstName || ''} {user.lastName || ''}
                            </Text>
                            <Text fontSize="sm" color="gray.600">{user.email}</Text>
                            <Text fontSize="xs" color="gray.500">{user.role} • {user.id.slice(0, 8)}...</Text>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {notifForm.singleUserId && (
                      <Box mt={2} p={2} bg="blue.50" borderRadius="md">
                        <HStack>
                          <Text fontSize="sm" fontWeight="medium">
                            Selected: {allUsers.find(u => u.id === notifForm.singleUserId)?.email || notifForm.singleUserId}
                          </Text>
                          <Tooltip label="Clear selection">
                            <IconButton
                              aria-label="Clear selection"
                              icon={<FiXIcon />}
                              size="sm"
                              variant="ghost"
                              minH="44px"
                              minW="44px"
                              onClick={() => setNotifForm(prev => ({ 
                                ...prev, 
                                singleUserId: '',
                                singleUserSearch: '',
                              }))}
                            />
                          </Tooltip>
                        </HStack>
                      </Box>
                    )}
                  </Box>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Segments (optional)</FormLabel>
                <HStack>
                  <Select
                    placeholder="Roles"
                    value=""
                    onChange={(e)=> {
                      const role = e.target.value as 'admin'|'technician'|'customer';
                      if (!role) return;
                      setNotifForm(prev => ({ ...prev, segments: { ...prev.segments, roles: Array.from(new Set([...(prev.segments.roles || []), role])) as any } }));
                    }}
                  >
                    <option value="admin">Admin</option>
                    <option value="technician">Technician</option>
                    <option value="customer">Customer</option>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Inactive days"
                    value={notifForm.segments.inactiveDays}
                    onChange={(e)=> setNotifForm(prev => ({ ...prev, segments: { ...prev.segments, inactiveDays: Number(e.target.value) || 0 } }))}
                    min={0}
                    w="180px"
                  />
                  <Select placeholder="Saved segments" onChange={(e)=> handleApplySegment(e.target.value)}>
                    {segmentsSaved.map(s => (
                      <option key={s.id} value={s.id}>{s.name || s.id}</option>
                    ))}
                  </Select>
                  <Tooltip label="Save Segment">
                    <IconButton
                      aria-label="Save Segment"
                      icon={<FiSaveIcon />}
                      onClick={handleSaveSegment}
                      isLoading={savingSegment}
                      colorScheme="blue"
                      variant="outline"
                    />
                  </Tooltip>
                </HStack>
                {notifForm.segments.roles.length > 0 && (
                  <HStack mt={2} spacing={2}>
                    {notifForm.segments.roles.map(r => (
                      <Badge key={r} colorScheme="blue">{r}</Badge>
                    ))}
                    <Tooltip label="Clear roles">
                      <IconButton
                        aria-label="Clear roles"
                        icon={<FiXIcon />}
                        size="sm"
                        variant="ghost"
                        minH="44px"
                        minW="44px"
                        onClick={()=> setNotifForm(prev => ({ ...prev, segments: { ...prev.segments, roles: [] as any } }))}
                      />
                    </Tooltip>
                  </HStack>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Delivery</FormLabel>
                <RadioGroup
                  value={notifForm.scheduleType}
                  onChange={(value: any)=> setNotifForm(prev => ({ ...prev, scheduleType: value }))}
                >
                  <Stack direction="row" spacing={6} align="center">
                    <Radio value="now">Send Now</Radio>
                    <HStack>
                      <Radio value="later">Schedule</Radio>
                      <Input
                        type="datetime-local"
                        value={notifForm.scheduledAt}
                        onChange={(e)=> setNotifForm(prev => ({ ...prev, scheduledAt: e.target.value }))}
                        isDisabled={notifForm.scheduleType !== 'later'}
                      />
                    </HStack>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Test Send (single user)</FormLabel>
                <HStack>
                  <Input
                    placeholder="Enter user email or UID"
                    value={testRecipient}
                    onChange={(e)=> setTestRecipient(e.target.value)}
                  />
                  <Tooltip label="Send Test Notification">
                    <IconButton
                      aria-label="Send Test Notification"
                      icon={<FiSendIcon />}
                      onClick={handleSendTest}
                      isLoading={sendingTest}
                      colorScheme="green"
                      variant="outline"
                    />
                  </Tooltip>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={1}>Queues a test notification document</Text>
              </FormControl>

              <Alert status="info">
                <AlertIcon />
                <Text fontSize="sm">
                  Notifications are sent via push notification to the mobile app
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setShowSendModal(false)}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSendNotification}
              isLoading={sending}
              loadingText="Sending"
              leftIcon={<FiSendIcon />}
            >
              Send Notification
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default NotificationsManagement;

